import { formatMessage } from '@/i18n/format'
import type { SurfaceCopy } from '@/i18n/min/surfaceTypes'
import type { CoachNote } from '@/api/learning'

function paramString(
  params: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const value = params?.[key]
  if (value == null) return undefined
  return String(value)
}

function joinQuotedWords(words: string[], andWord: string): string {
  if (words.length === 0) return ''
  if (words.length === 1) return `«${words[0]}»`
  if (words.length === 2) return `«${words[0]}» ${andWord} «${words[1]}»`
  const head = words
    .slice(0, -1)
    .map((w) => `«${w}»`)
    .join(', ')
  return `${head}, ${andWord} «${words[words.length - 1]}»`
}

function confusionWords(
  params: Record<string, unknown>,
  andWord: string,
): string | undefined {
  const raw = params.wordTexts
  if (Array.isArray(raw) && raw.every((w) => typeof w === 'string')) {
    const joined = joinQuotedWords(raw as string[], andWord)
    if (joined) return joined
  }
  return paramString(params, 'words')
}

export function formatCoachNoteMessage(
  note: Pick<CoachNote, 'type' | 'message' | 'messageParams'>,
  coach: SurfaceCopy['coach'],
): string {
  const params = note.messageParams ?? {}
  switch (note.type) {
    case 'CONFUSION_CLUSTER': {
      const words = confusionWords(params, coach.andWord)
      if (!words) return note.message
      return formatMessage(coach.confusionCluster, { words })
    }
    case 'FORM_GAP': {
      const surface = paramString(params, 'surface')
      const count = paramString(params, 'count')
      if (!surface || count == null) return note.message
      const formsHint = paramString(params, 'formsHint') ?? ''
      const lemma = paramString(params, 'lemma')
      if (lemma) {
        return formatMessage(coach.formGap, { surface, count, lemma, formsHint })
      }
      return formatMessage(coach.formGapNoLemma, { surface, count, formsHint })
    }
    case 'SYNONYM_MIXUP': {
      const word1 = paramString(params, 'word1')
      const word2 = paramString(params, 'word2')
      if (word1 && word2) {
        return formatMessage(coach.synonymMixup, { word1, word2 })
      }
      return coach.synonymMixupGeneric
    }
    case 'NEGLECTED_WORD': {
      const days = paramString(params, 'days')
      if (days == null) return note.message
      const word = paramString(params, 'word')
      if (word) {
        return formatMessage(coach.neglectedWord, { word, days })
      }
      return formatMessage(coach.neglectedWordGeneric, { days })
    }
    default:
      return note.message
  }
}

export function formatCoachNoteCta(
  ctaAction: CoachNote['ctaAction'],
  coach: SurfaceCopy['coach'],
  fallback: string,
): string {
  switch (ctaAction) {
    case 'PRACTICE':
      return coach.ctaPractice
    case 'WORD_DETAIL':
      return coach.ctaOpenWord
    case 'CHALLENGE':
      return coach.ctaChallenge
    default:
      return fallback
  }
}
